import { useEffect, useMemo, useState } from "react";
import {
  deleteAccount,
  generateAvatar,
  loadAuthorProfile,
  removeAvatar,
  updateProfile,
  type NovelSummary
} from "../apiClient.js";
import { localizeGenre } from "../lib/storyOptions.js";
import { EditProfileModal } from "../components/EditProfileModal.js";
import { ReportDialog } from "../components/ReportDialog.js";
import { FlagIcon } from "../components/FlagIcon.js";
import { CineBackdrop } from "../components/CineBackdrop.js";
import { FilmCard } from "../components/FilmCard.js";
import { ReelSelect } from "../components/ReelSelect.js";
import { ReelChipSkeleton } from "../components/ReelChipSkeleton.js";
import { PROFILE_SORT_OPTIONS, sortNovels, type NovelSortKey } from "../lib/novelSort.js";
import { track } from "../lib/analytics.js";
import { useLanguage, useT } from "../lib/i18n/store.js";
import { fallBackToSource, sizedImageUrl } from "../lib/imageUrl.js";
import { isValidSupportLink, isValidUsername } from "@lorevok/domain";
import { AUTHOR_SUPPORT_LINKS_ENABLED } from "../lib/features.js";
import { authorPageMeta, missingPageMeta, usePageMeta } from "../lib/pageMeta.js";

/** First letter of the display name for the avatar placeholder ("?" when empty). */
function avatarInitial(name: string): string {
  const first = [...name.trim()][0];
  return first ? first.toUpperCase() : "?";
}

/** Formats the join date as a localized month + year, or null when unusable. */
function formatJoined(iso: string | null, lang: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  // Format WITH the day, then drop it: a month-and-year pattern uses the
  // standalone month ("сентябрь 2026"), but "since …" needs the form a full date
  // takes ("с сентября 2026"). Other languages come out the same either way.
  const parts = new Intl.DateTimeFormat(lang, {
    day: "numeric",
    month: "long",
    year: "numeric"
  }).formatToParts(date);
  const dayIndex = parts.findIndex((part) => part.type === "day");
  if (dayIndex === -1) return parts.map((part) => part.value).join("");
  const dropLiteral = parts[dayIndex + 1]?.type === "literal" ? 1 : 0;
  return parts
    .filter((_, index) => index < dayIndex || index > dayIndex + dropLiteral)
    .map((part) => part.value)
    .join("")
    .trim();
}

/** Box-office stat chip — a big tabular figure over an uppercase micro-label. */
function StatChip({ figure, label }: { figure: string; label: string }) {
  return (
    <div className="reel-chip">
      <span className="reel-chip-figure">{figure}</span>
      <span className="reel-chip-label">{label}</span>
    </div>
  );
}

/**
 * Public author profile, art-directed as a director's page: a cinematic
 * "director band" hero (avatar + name + statement over a blurred backdrop), a
 * box-office stat ribbon of aggregate reach, a "now showing" spotlight for the
 * author's top novel, and a poster-style filmography grid. Viewing your OWN
 * profile opens the name/bio/avatar editor in a popup.
 */
export function AuthorPage({
  authorId,
  currentOwnerId,
  onProfileChanged,
  onPlay,
  onOpenDetail,
  onCreate,
  onBack,
  accountEmail = null,
  onAccountDeleted,
  avatarCostCredits = null,
  onCreditsSpent
}: {
  /** The /u/ key: the author's username, or their id (old links, profile-less owners). */
  authorId: string | null;
  currentOwnerId: string | null;
  /** Signed-in account email; enables "Delete account" in the profile editor. */
  accountEmail?: string | null;
  /** Called after the account was deleted (sign out + leave). */
  onAccountDeleted?: () => void;
  /** Credit price of a generated avatar, shown on the editor's button. */
  avatarCostCredits?: number | null;
  /** A paid action ran (or was refunded): the app re-reads the credit balance. */
  onCreditsSpent?: () => void;
  /** Notifies the app the profile changed, so the topbar identity refreshes. */
  onProfileChanged?: () => void;
  onPlay: (novelId: string) => void;
  onOpenDetail: (novelId: string) => void;
  onCreate: () => void;
  onBack: () => void;
}) {
  const t = useT();
  const lang = useLanguage();
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  // The resolved account id — authorId is the /u/ key, usually the username.
  const [profileId, setProfileId] = useState<string | null>(null);
  const [bio, setBio] = useState("");
  const [supportLink, setSupportLink] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [novels, setNovels] = useState<NovelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // The API gave no answer: the empty profile on screen is a stand-in, not a
  // page without novels, so it must not be marked noindex.
  const [unavailable, setUnavailable] = useState(false);
  const [editing, setEditing] = useState(false);
  const [sort, setSort] = useState<NovelSortKey>("newest");

  useEffect(() => {
    if (!authorId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setProfileId(null);
    void loadAuthorProfile(authorId).then((profile) => {
      if (cancelled) return;
      setNotFound(profile.notFound === true);
      setUnavailable(profile.unavailable === true);
      setProfileId(profile.id || authorId);
      setUsername(profile.username);
      setDisplayName(profile.displayName);
      setBio(profile.bio);
      setSupportLink(profile.supportLink);
      setAvatarUrl(profile.avatarUrl);
      setCreatedAt(profile.createdAt);
      setNovels(profile.novels);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [authorId]);

  const resolvedAuthorId = profileId ?? authorId;
  const isOwnProfile = currentOwnerId !== null && resolvedAuthorId === currentOwnerId;
  const [reporting, setReporting] = useState(false);
  const sortedNovels = useMemo(() => sortNovels(novels, sort), [novels, sort]);

  async function saveProfile(name: string, newBio: string, newSupportLink: string): Promise<void> {
    const profile = await updateProfile({ displayName: name, bio: newBio, supportLink: newSupportLink });
    setDisplayName(profile.displayName);
    setBio(profile.bio);
    setSupportLink(profile.supportLink);
    onProfileChanged?.();
  }

  async function makeAvatar(prompt: string): Promise<string | null> {
    try {
      const profile = await generateAvatar(prompt);
      track("avatar_generated");
      setAvatarUrl(profile.avatarUrl);
      onProfileChanged?.();
      return profile.avatarUrl;
    } finally {
      // A paid action: the balance changed — or, on a failure, the charge was
      // refunded — so the topbar balance is re-read either way.
      onCreditsSpent?.();
    }
  }

  async function clearAvatar(): Promise<string | null> {
    const profile = await removeAvatar();
    setAvatarUrl(profile.avatarUrl);
    onProfileChanged?.();
    return profile.avatarUrl;
  }

  // Aggregate reach across the author's published novels — the box-office ribbon.
  const totalViews = novels.reduce((sum, novel) => sum + (novel.views ?? 0), 0);
  const totalLikes = novels.reduce((sum, novel) => sum + (novel.likes ?? 0), 0);
  const totalFinished = novels.reduce((sum, novel) => sum + (novel.completions ?? 0), 0);
  // Backdrop = the author's most-viewed cover (fall back to the first with one).
  const backdropCover =
    [...novels].sort((a, b) => b.views - a.views).find((novel) => novel.cover_asset_url)
      ?.cover_asset_url ??
    novels.find((novel) => novel.cover_asset_url)?.cover_asset_url ??
    null;
  // Spotlight the top novel — but only with a real shelf behind it, so a lone
  // card is never duplicated as its own "feature".
  const featured =
    novels.length >= 3
      ? [...novels].sort((a, b) => b.views - a.views || b.likes - a.likes)[0]
      : null;
  const joined = formatJoined(createdAt, lang);

  // The head: the author's name, bio and canonical /u/<username> once loaded;
  // out of the index while they have no public novel, or when the link is
  // stale. A profile the API could not be asked for changes nothing.
  usePageMeta(
    loading || !authorId || unavailable
      ? null
      : notFound
        ? missingPageMeta(t("author.notFoundTitle"), t)
        : authorPageMeta(
            { id: resolvedAuthorId ?? authorId, username, displayName, bio, publicNovels: novels.length },
            t
          )
  );

  // A mistyped or stale /u/ link: say so, rather than showing an empty "Author".
  if (notFound && !loading) {
    return (
      <section className="page-section">
        <button type="button" className="detail-back-btn reel-back" onClick={onBack}>
          {t("author.back")}
        </button>
        <div className="empty-state reel-empty author-not-found" role="status">
          <img src="/assets/empty-state.png" alt="" className="empty-state-art" />
          <h1 className="empty-state-title">{t("author.notFoundTitle")}</h1>
          <p className="empty-state-message">{t("author.notFoundText")}</p>
          <button type="button" className="empty-state-cta" onClick={onBack}>
            {t("author.notFoundCta")}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="page-section">
      <button type="button" className="detail-back-btn reel-back" onClick={onBack}>
        {t("author.back")}
      </button>

      <div className="author-hero author-hero--cinema">
        <CineBackdrop cover={backdropCover} />
        <div className="author-hero-content">
          {/* Avatar slot — the uploaded image, or an initials placeholder. */}
          <div className={loading && !avatarUrl ? "author-avatar skeleton" : "author-avatar"}>
            {avatarUrl ? (
              <img src={sizedImageUrl(avatarUrl, 128, { aspect: 1 })} onError={fallBackToSource(avatarUrl)} alt="" />
            ) : (
              <span aria-hidden="true">{loading ? "" : avatarInitial(displayName)}</span>
            )}
          </div>
          <div className="author-header-text">
            <p className="marquee-eyebrow">
              <span className="reel-dot" aria-hidden="true" />
              {t("author.eyebrow")}
            </p>
            <h1>
              {loading ? (
                <>
                  <span className="sr-only">{t("detail.loading")}</span>
                  <span className="skeleton author-name-skeleton" aria-hidden="true" />
                </>
              ) : (
                displayName
              )}
            </h1>
            {!loading && isValidUsername(username) && <p className="author-username">@{username}</p>}
            {!loading && bio && <p className="author-bio">{bio}</p>}
            {AUTHOR_SUPPORT_LINKS_ENABLED && !loading && supportLink && isValidSupportLink(supportLink) && (
              <a className="support-author-link author-support" href={supportLink} target="_blank" rel="noopener noreferrer nofollow">
                {t("author.supportAuthor")}
              </a>
            )}
            {loading ? (
              <div className="skeleton author-meta-skeleton" aria-hidden="true" />
            ) : (
              joined && (
                <p className="metrics" role="status">
                  {t("author.memberSince", { date: joined })}
                </p>
              )
            )}
          </div>
          {isOwnProfile && !loading && (
            <button
              type="button"
              className="secondary author-edit-btn"
              onClick={() => setEditing(true)}
            >
              {t("author.editProfile")}
            </button>
          )}
          {!isOwnProfile && !loading && resolvedAuthorId && (
            <button
              type="button"
              className="detail-icon-btn author-report"
              aria-label={t("report.profileAction")}
              title={t("report.profileAction")}
              onClick={() => setReporting(true)}
            >
              <FlagIcon />
            </button>
          )}
        </div>
      </div>
      {reporting && resolvedAuthorId && (
        <ReportDialog targetType="profile" targetId={resolvedAuthorId} onClose={() => setReporting(false)} />
      )}

      {editing && (
        <EditProfileModal
          initialName={displayName}
          username={username}
          initialBio={bio}
          initialSupportLink={supportLink}
          avatarUrl={avatarUrl}
          onSaveProfile={saveProfile}
          onGenerateAvatar={makeAvatar}
          onRemoveAvatar={clearAvatar}
          onClose={() => setEditing(false)}
          accountEmail={accountEmail}
          avatarCostCredits={avatarCostCredits}
          onDeleteAccount={async () => {
            await deleteAccount();
            setEditing(false);
            onAccountDeleted?.();
          }}
        />
      )}

      {/* While loading, the ribbon and the toolbar hold their places so the
          grid below doesn't drop when they arrive. */}
      {loading && (
        <div className="reel-ribbon reel-chip-row" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, index) => (
            <ReelChipSkeleton key={index} />
          ))}
        </div>
      )}
      {!loading && novels.length > 0 && (
        <div className="reel-ribbon reel-chip-row">
          <StatChip figure={novels.length.toLocaleString(lang)} label={t("author.statNovels")} />
          <StatChip figure={totalViews.toLocaleString(lang)} label={t("author.statViews")} />
          <StatChip figure={totalLikes.toLocaleString(lang)} label={t("author.statLikes")} />
          <StatChip figure={totalFinished.toLocaleString(lang)} label={t("author.statFinished")} />
        </div>
      )}

      {!loading && featured && (
        <section className="marquee-feature">
          <CineBackdrop cover={featured.cover_asset_url ?? null} />
          <div className="marquee-poster">
            <FilmCard novel={featured} lang={lang} t={t} onPlay={onPlay} onOpenDetail={onOpenDetail} />
          </div>
          <div className="marquee-credit">
            <div className="marquee-credit-head">
              <p className="marquee-eyebrow">
                <span className="reel-dot" aria-hidden="true" />
                {t("author.nowShowing")}
              </p>
            </div>
            <h2>{featured.title}</h2>
            {featured.genre.length > 0 && (
              <p className="marquee-tagline">
                {featured.genre.map((tag) => localizeGenre(tag, t)).join(" · ")}
              </p>
            )}
            <p className="marquee-desc">{featured.description}</p>
            <div className="marquee-actions">
              <button type="button" onClick={() => onPlay(featured.id)}>
                {t("common.play")}
              </button>
              <button type="button" className="secondary" onClick={() => onOpenDetail(featured.id)}>
                {t("common.details")}
              </button>
            </div>
          </div>
        </section>
      )}

      {loading && (
        <div className="reel-toolbar" aria-hidden="true">
          <span className="skeleton reel-heading-skeleton" />
          <span className="skeleton reel-select-skeleton" />
        </div>
      )}
      {!loading && novels.length > 0 && (
        <div className="reel-toolbar">
          <h2 className="reel-section-heading">{t("author.novelsHeading")}</h2>
          <div className="reel-toolbar-controls">
            <span className="reel-toolbar-label" aria-hidden="true">
              {t("author.sortLabel")}
            </span>
            <ReelSelect
              value={sort}
              options={PROFILE_SORT_OPTIONS.map((option) => ({ key: option.key, label: t(option.labelKey) }))}
              onChange={setSort}
              label={t("author.sortLabel")}
            />
          </div>
        </div>
      )}

      <div aria-busy={loading}>
        {loading ? (
          <div className="film-grid" data-testid="author-skeleton" aria-hidden="true">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="film-card film-card--skeleton skeleton" />
            ))}
          </div>
        ) : novels.length === 0 ? (
          <div className="empty-state reel-empty">
            <img src="/assets/empty-state.png" alt="" className="empty-state-art" />
            <p className="empty-state-message">{t("author.empty")}</p>
            {isOwnProfile && (
              <button type="button" className="empty-state-cta" onClick={onCreate}>
                {t("common.createNovel")}
              </button>
            )}
          </div>
        ) : (
          <div className="film-grid">
            {sortedNovels.map((novel) => (
              <FilmCard
                key={novel.id}
                novel={novel}
                lang={lang}
                t={t}
                onPlay={onPlay}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
